import { useEffect, useState, useRef, useCallback } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Download,
  Play,
  ArrowRightLeft,
  FileX,
} from "lucide-react";
import type { IepDocument, IepSection } from "@/types/api";
import {
  getIepDocument,
  getIepSections,
  getDownloadUrl,
  reprocessIep,
  getIepDocuments,
} from "../api/iep-documents-api";
import { getChild, setCurrentIep } from "@/features/children/api/children-api";
import { usePolling } from "@/hooks/use-polling";
import { Badge } from "@/components/ui/badge";
import { Markdown } from "@/components/ui/markdown";
import { useIepAnalysis } from "../hooks/use-iep-analysis";
import { AnalysisTab } from "./analysis-tab";
import { Button } from "@/components/ui/button";
import { Notice } from "@/components/ui/notice";
import { Spinner } from "@/components/ui/spinner";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { PdfViewer } from "@/components/ui/pdf-viewer";
import { usePageTitle } from "@/hooks/use-page-title";
import { ProgressReportsTab } from "@/features/progress-reports/components/progress-reports-tab";
import { AskAdvocateButton } from "@/features/advocate/components/ask-advocate-button";
import { formatDate } from "@/lib/format-date";
import { documentMeetingTypeLabel } from "@/lib/meeting-labels";

/** `#goal-340` (an advocate citation) opens the analysis tab on the goal list and scrolls to that goal. */
const GOAL_HASH = /^#goal-\d+$/;

export function IepViewerPage() {
  const { t } = useTranslation(["iep-documents", "common"]);
  usePageTitle("IEP");
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const goalDeepLink = GOAL_HASH.test(hash);
  const documentId = Number(id);
  const [document, setDocument] = useState<IepDocument | null>(null);
  const [sections, setSections] = useState<IepSection[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<
    "document" | "analysis" | "progress-reports"
  >(goalDeepLink ? "analysis" : "document");
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [otherIeps, setOtherIeps] = useState<IepDocument[]>([]);
  const [childCurrentIepId, setChildCurrentIepId] = useState<number | null>(null);
  const [childRole, setChildRole] = useState<string | null>(null);
  const [settingCurrent, setSettingCurrent] = useState(false);
  const compareRef = useRef<HTMLDivElement>(null);

  const {
    run: analysisRun,
    source: analysisSource,
    sections: analysisSections,
    goalAnalyses,
    otherSources: analysisOtherSources,
    stale: analysisStale,
    isLoading: analysisLoading,
    loadError: analysisLoadError,
    isTriggering,
    triggerError,
    trigger: triggerAnalysis,
    reload: reloadAnalysis,
  } = useIepAnalysis(document?.childProfileId ?? 0, documentId);

  // Meeting Prep lives at the child level now (standalone tab), so it is no
  // longer rendered embedded inside the document viewer.

  const loadSections = useCallback(async () => {
    const secRes = await getIepSections(documentId);
    if (secRes.success && secRes.data) {
      setSections(secRes.data);
    }
  }, [documentId]);

  useEffect(() => {
    async function load() {
      try {
        const docRes = await getIepDocument(documentId);
        if (docRes.success && docRes.data) {
          setDocument(docRes.data);

          if (docRes.data.status === "parsed") {
            await loadSections();
          }
        }
      } catch {
        // handled
      } finally {
        setIsLoading(false);
      }
    }
    load();
  }, [documentId, loadSections]);

  // Poll for document processing completion
  const pollDocumentStatus = useCallback(async () => {
    const res = await getIepDocument(documentId);
    if (res.success && res.data) {
      setDocument(res.data);
      if (res.data.status === "parsed") {
        await loadSections();
      }
    }
  }, [documentId, loadSections]);

  usePolling(pollDocumentStatus, 5000, document?.status === "processing");

  // Bring the deep-linked goal card into view once the analysis has rendered it.
  useEffect(() => {
    if (!goalDeepLink || activeTab !== "analysis" || !goalAnalyses || goalAnalyses.length === 0) return;
    const el = window.document.getElementById(hash.slice(1));
    if (el && typeof el.scrollIntoView === "function") el.scrollIntoView({ block: "start" });
  }, [goalDeepLink, hash, activeTab, goalAnalyses]);

  // Load other IEPs for comparison when document is available
  useEffect(() => {
    if (!document?.childProfileId) return;
    async function loadOthers() {
      try {
        const res = await getIepDocuments(document!.childProfileId);
        if (res.success && res.data) {
          setOtherIeps(res.data.filter((d) => d.id !== documentId));
        }
      } catch {
        // non-critical
      }
    }
    loadOthers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.childProfileId, documentId]);

  // Load child profile to know whether this IEP is "current"
  useEffect(() => {
    if (!document?.childProfileId) return;
    async function loadChild() {
      try {
        const res = await getChild(document!.childProfileId);
        if (res.success && res.data) {
          setChildCurrentIepId(res.data.currentIepDocumentId);
          setChildRole(res.data.role);
        }
      } catch {
        // non-critical
      }
    }
    loadChild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.childProfileId]);

  const handleMakeCurrent = async () => {
    if (!document) return;
    setSettingCurrent(true);
    try {
      const res = await setCurrentIep(document.childProfileId, document.id);
      if (res.success) setChildCurrentIepId(document.id);
    } catch {
      // handled by interceptor
    } finally {
      setSettingCurrent(false);
    }
  };

  // Close compare dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (
        compareRef.current &&
        !compareRef.current.contains(e.target as Node)
      ) {
        setCompareOpen(false);
      }
    }
    if (compareOpen) {
      window.addEventListener("mousedown", handleClickOutside);
      return () => window.removeEventListener("mousedown", handleClickOutside);
    }
  }, [compareOpen]);

  const handleDownload = async () => {
    const res = await getDownloadUrl(documentId);
    if (res.success && res.data) {
      window.open(res.data.url, "_blank");
    }
  };

  const handleReprocess = async () => {
    await reprocessIep(documentId);
    setDocument((prev) => (prev ? { ...prev, status: "processing" } : prev));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner label={t("loadingDocument")} />
      </div>
    );
  }

  if (!document) {
    return <EmptyState icon={FileX} title={t("documentNotFound")} />;
  }

  const documentTitle =
    document.fileName ||
    (document.meetingType
      ? documentMeetingTypeLabel(document.meetingType)
      : t("viewer.documentIdFallback", { id: document.id }));

  return (
    // Document viewer: cap at a comfortable reading width rather than filling
    // the widened (max-w-7xl) app shell.
    <div className="space-y-4 max-w-5xl">
      <Link
        to={`/children/${document.childProfileId}`}
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-brand-slate-500 hover:text-brand-teal-500 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" strokeWidth={1.8} aria-hidden="true" />
        {t("backToChild")}
      </Link>

      <PageHeader
        title={documentTitle}
        actions={
          <>
            <AskAdvocateButton
              childId={document.childProfileId}
              about={{ kind: "iep", id: document.id }}
              label={document.iepDate ? t("askAbout.iep", { date: formatDate(document.iepDate) }) : undefined}
              canAsk={childRole !== null && childRole !== "viewer"}
              data-testid="iep-ask-advocate"
            />
            {otherIeps.length > 0 && (
              <div className="relative" ref={compareRef}>
                <Button
                  variant="secondary"
                  onClick={() => setCompareOpen(!compareOpen)}
                  data-testid="compare-button"
                >
                  <ArrowRightLeft
                    className="w-4 h-4 mr-1.5"
                    strokeWidth={1.8}
                    aria-hidden="true"
                  />
                  {t("viewer.compare")}
                </Button>
                {compareOpen && (
                  <div className="absolute right-0 top-full mt-1 w-64 bg-white rounded-card border border-brand-slate-200 shadow-lg z-20 py-1">
                    <p className="px-3 py-1.5 text-[11px] text-brand-slate-500 uppercase tracking-wide font-semibold">
                      {t("viewer.compareWith")}
                    </p>
                    {otherIeps.map((other) => (
                      <button
                        key={other.id}
                        className="w-full text-left px-3 py-2 text-sm text-brand-slate-700 hover:bg-brand-slate-50 transition-colors"
                        onClick={() => {
                          setCompareOpen(false);
                          navigate(
                            `/children/${document.childProfileId}/compare/${documentId}/${other.id}`,
                          );
                        }}
                      >
                        <span className="font-medium">
                          {other.iepDate
                            ? formatDate(other.iepDate)
                            : t("viewer.documentIdFallback", { id: other.id })}
                        </span>
                        {other.meetingType && (
                          <span className="text-brand-slate-500 ml-2 text-[12px]">
                            {documentMeetingTypeLabel(other.meetingType)}
                          </span>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <Button variant="ghost" onClick={handleDownload}>
              <Download
                className="w-4 h-4 mr-1.5"
                strokeWidth={1.8}
                aria-hidden="true"
              />
              {t("viewer.downloadPdf")}
            </Button>
            {(document.status === "error" || document.status === "uploaded") && (
              <Button onClick={handleReprocess}>
                <Play
                  className="w-4 h-4 mr-1.5"
                  strokeWidth={1.8}
                  aria-hidden="true"
                />
                {t("viewer.process")}
              </Button>
            )}
          </>
        }
      />

      <div className="flex items-center gap-3 flex-wrap">
        {document.meetingType && (
          <Badge variant="neutral">
            {documentMeetingTypeLabel(document.meetingType)}
          </Badge>
        )}
        {childCurrentIepId === document.id && (
          <Badge variant="success" data-testid="current-iep-badge">
            {t("viewer.currentIep")}
          </Badge>
        )}
        {childCurrentIepId !== document.id && childRole && childRole !== "viewer" && (
          <Button
            variant="ghost"
            size="sm"
            onClick={handleMakeCurrent}
            loading={settingCurrent}
            data-testid="make-current-iep-button"
          >
            {t("viewer.makeCurrent")}
          </Button>
        )}
        {document.iepDate && (
          <span className="text-[13px] text-brand-slate-500">
            {formatDate(document.iepDate)}
          </span>
        )}
        {document.attendees && (
          <span className="text-[13px] text-brand-slate-500">
            {t("viewer.attendees", { attendees: document.attendees })}
          </span>
        )}
      </div>

      {document.notes && (
        <div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setNotesExpanded(!notesExpanded)}
          >
            {notesExpanded ? (
              <ChevronUp className="w-3.5 h-3.5 mr-1" strokeWidth={1.8} aria-hidden="true" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 mr-1" strokeWidth={1.8} aria-hidden="true" />
            )}
            {t("viewer.notes")}
          </Button>
          {notesExpanded && (
            <div className="mt-1 bg-brand-slate-50 rounded-card p-3 border border-brand-slate-200">
              <Markdown content={document.notes ?? ''} className="text-sm text-brand-slate-600" />
            </div>
          )}
        </div>
      )}

      {document.status === "processing" && (
        <Notice variant="warning" title={t("viewer.processingTitle")}>
          {t("viewer.processingBody")}
        </Notice>
      )}

      {document.status === "error" && (
        <Notice variant="error" title={t("viewer.processingFailedTitle")}>
          {t("viewer.processingFailedBody")}
        </Notice>
      )}

      {document.status === "uploaded" && (
        <Notice variant="info" title={t("viewer.notProcessedTitle")}>
          {t("viewer.notProcessedBody")}
        </Notice>
      )}

      {document.status === "parsed" && sections.length > 0 && (
        <>
          {/* Tab bar */}
          <div className="flex border-b border-brand-slate-200">
            <button
              onClick={() => setActiveTab("document")}
              data-testid="tab-document"
              className={`px-4 py-2 text-[13px] font-medium transition-colors ${
                activeTab === "document"
                  ? "text-brand-slate-800 border-b-2 border-brand-teal-500"
                  : "text-brand-slate-500 hover:text-brand-slate-800"
              }`}
            >
              {t("viewer.tabDocument")}
            </button>
            <button
              onClick={() => setActiveTab("analysis")}
              data-testid="tab-analysis"
              className={`px-4 py-2 text-[13px] font-medium transition-colors ${
                activeTab === "analysis"
                  ? "text-brand-slate-800 border-b-2 border-brand-teal-500"
                  : "text-brand-slate-500 hover:text-brand-slate-800"
              }`}
            >
              {t("viewer.tabAnalysis")}
              {analysisSource?.status === "Completed" && (
                <span className="ml-2 inline-block w-2 h-2 rounded-full bg-brand-teal-500" />
              )}
            </button>
            <button
              onClick={() => setActiveTab("progress-reports")}
              data-testid="tab-progress-reports"
              className={`px-4 py-2 text-[13px] font-medium transition-colors ${
                activeTab === "progress-reports"
                  ? "text-brand-slate-800 border-b-2 border-brand-teal-500"
                  : "text-brand-slate-500 hover:text-brand-slate-800"
              }`}
            >
              {t("viewer.tabProgressReports")}
            </button>
          </div>

          {/* Tab content */}
          {activeTab === "document" && (
            <PdfViewer
              fileName={document.fileName}
              parsedNote={
                sections.length > 0
                  ? t("viewer.parsedNote")
                  : undefined
              }
              loadUrl={async () => {
                const res = await getDownloadUrl(documentId);
                return res.success && res.data ? res.data.url : null;
              }}
            />
          )}

          {activeTab === "analysis" && (
            <AnalysisTab
              childId={document.childProfileId}
              run={analysisRun}
              source={analysisSource}
              sections={analysisSections}
              goalAnalyses={goalAnalyses}
              otherSources={analysisOtherSources}
              stale={analysisStale}
              isLoading={analysisLoading}
              loadError={analysisLoadError}
              isTriggering={isTriggering}
              triggerError={triggerError}
              onTrigger={triggerAnalysis}
              onReload={reloadAnalysis}
              initialView={goalDeepLink ? "goals" : undefined}
              canAsk={childRole !== null && childRole !== "viewer"}
            />
          )}

          {activeTab === "progress-reports" && (
            <ProgressReportsTab
              iepId={documentId}
              childId={document.childProfileId}
              canEdit={childRole !== null && childRole !== "viewer"}
            />
          )}
        </>
      )}
    </div>
  );
}
